import { CarsStore } from './cars-store';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { CarSearchV3, ContextInput } from 'src/common/__generated__/api';
import { action, observable, runInAction } from 'mobx';
import { getBexApiContext } from 'bernie-context';
import { getErrorString, SystemEvent } from 'bernie-logger';
import { SPINNERS_ERRORS, STORE_EVENTS } from './store-event';
import { IErrorState, ISearchErrorState } from 'src/stores/store-event';
import analyticsUtil from 'utils/analytics-util';
import { formatText } from 'bernie-l10n';
import { MultiItemStore, SearchCriteriaStore } from './client';
import { ErrorType } from 'components/common/ErrorType';
import { Level, liveAnnounce } from 'uitk-react-utils';

export class NewCarSearchListStore extends CarsStore {
  private context: ContextInput;
  public searchOfferScrollPos: number;

  private API_TIMEOUT:number = 30000
  private RETRY_COUNT:number = 1

  @observable
  public carSearchResult: CarSearchV3.CarSearchResults;

  @observable
  public response: any;

  @observable
  public errorInFetchingResult: boolean = false;

  @observable
  public parsedErrorInfo: IErrorState = {} as IErrorState;

  @observable
  public offers: CarSearchV3.Listings[];

  @observable
  public sortAndFilter: CarSearchV3.SortAndFilter;

  @observable
  public appliedSortAndFilter: CarSearchV3.AppliedSortAndFilters[];

  @observable
  public map: CarSearchV3.Map;
  
  @observable
  public mapError: string;

  @observable
  public loadMore: CarSearchV3.LoadMoreAction;
  
  @observable
  public errorContent: CarSearchV3.CarsErrorContent;

  @observable
  public  recommendationContent: CarSearchV3.CarRecommendationContent[];

  @observable
  public recommendationsOnLimitedSearchResults: CarSearchV3.CarRecommendationsInlineFragment[];

  @observable
  public isLoading: boolean = true;

  @observable
  public expandedItem: object = {};

  @observable
  public timeOutCompleted: boolean;

  @observable
  public carRentalLocation: CarSearchV3.CarRentalLocations;

  @observable
  public shouldResetPage: boolean = true;

  @observable
  public inRecoverableErrorState: boolean = false;

  private pickupLoc: string;

  private dropLoc: string;

  private invalidFieldsInitialState = (): ISearchErrorState => ({
    pickUpLoc  : false,
    pickUpDate : false,
    dropDate   : false,
    pickUpTime : false,
    dropTime   : false,
    dropLoc    : false,
    emptyState : false,
    errorType  : null
  });

  private isInvalidField: ISearchErrorState = this.invalidFieldsInitialState();

  public constructor(bexConfigs, logger, appconfig: SerializedData, param) {
    super(bexConfigs, logger, appconfig, param);
    this.logger = logger;
  }

  public setContext(context: ContextStore) {
    this.context = getBexApiContext(context);
  }

  public failureHandler(
    variable: any,
    error: any,
    event: SystemEvent,
    analytics: AnalyticsStore,
    pageId: string,
    deviceId: string
  ) {
    this.logger.logEvent(event, JSON.stringify(variable), getErrorString(error));
    analyticsUtil.publicCustomClientSideAnalyticsPayload(pageId, analytics, deviceId, event.name);
  }

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
    this.context = { ...data.context };
  }

  /**
   * @param id
   * keeping expando hashmap with filter/sort as key and value as true, and on toggle if same id exists, deleting that
   * key from expandedItem
   */
  @action
  public setExpandedItem = (id: string) => {
    if (this.expandedItem[id]) {
      delete this.expandedItem[id];
    } else {
      this.expandedItem[id] = true;
    }
    this.expandedItem = { ...this.expandedItem };
  };

  public resetRentalLocations = (searchCriteriaStore: SearchCriteriaStore) => {
    const {
      searchQuery: { pickUpLoc, dropLoc }
    } = searchCriteriaStore;
    if (
      pickUpLoc !== undefined &&
      this.pickupLoc !== undefined &&
      pickUpLoc.toLowerCase() !== this.pickupLoc.toLowerCase()
    ) {
      this.carRentalLocation = {
        ...this.carRentalLocation,
        pickUpLocation : {
          __typename : 'CarSearchLocation',
          shortName  : null,
          fullName   : null
        }
      };
    }
    if (dropLoc !== undefined && this.dropLoc !== undefined && dropLoc.toLowerCase() !== this.dropLoc.toLowerCase()) {
      this.carRentalLocation = {
        ...this.carRentalLocation,
        dropOffLocation : {
          __typename : 'CarSearchLocation',
          shortName  : null,
          fullName   : null
        }
      };
    }
    this.pickupLoc = pickUpLoc;
    this.dropLoc = dropLoc;
  };

  public getMultiItemPlayBackTitleBar = () => this.carSearchResult?.multiItemPlayBackTitleBar;
  public getChangeRentalLocation = () => this.carSearchResult?.changeRentalLocation;
  public getMultiItemPickUpDropOffMessage = () => (this.carSearchResult?.multiItemPickUpDropOffMessage?.message as CarSearchV3.CarPhraseTextInlineFragment)?.text;

  public resetResults(searchCriteriaStore: SearchCriteriaStore) {
    this.isLoading = !searchCriteriaStore.isSortAndFilterChanging;
    this.errorInFetchingResult = false;
    this.parsedErrorInfo = null;
    this.mapError = null;
    this.isInvalidField = this.invalidFieldsInitialState();
    if (!searchCriteriaStore.showMoreSelected && !searchCriteriaStore.isSortAndFilterChanging) {
      this.offers = null;
      this.searchOfferScrollPos = null;
      this.carSearchResult = null;
      this.resetRentalLocations(searchCriteriaStore);
      // this.carRentalLocation = null;
      this.loadMore = null;
    }
  }

  private resetMap(searchVariables){
    if (this.map && searchVariables.primaryCarSearchCriteria.pickUpLocation.coordinates) {
      this.map.markers = null;
      this.map.center = { ...searchVariables.primaryCarSearchCriteria.pickUpLocation.coordinates };
    }
    if (this.errorInFetchingResult){
      this.mapError = this.parsedErrorInfo.message;
    } else if (this.errorContent){
      this.mapError = `${this.errorContent.heading}${this.errorContent.subText ? ': ' : ''}${this.errorContent.subText}`;
    }
  }

  private setRecoverableErrorState = (searchCriteriaStore: SearchCriteriaStore) => {
    let recoverableError;
    this.inRecoverableErrorState = true;
    //Set sortAndFilter to the last sortAndFilter returned from api response in order to revert selections
    this.sortAndFilter = { ...this.carSearchResult.sortAndFilter };
    if (searchCriteriaStore.isSortAndFilterChanging) {
      recoverableError = ErrorType.SortAndFilterError;
    }
    if (searchCriteriaStore.showMoreSelected) {
      recoverableError = ErrorType.PaginationError;
    }
    if (searchCriteriaStore.isLoyaltyToggleChanging && searchCriteriaStore.useRewards) {
      recoverableError = ErrorType.ApplyRewardsError;
    }
    if (searchCriteriaStore.isLoyaltyToggleChanging && !searchCriteriaStore.useRewards) {
      recoverableError = ErrorType.RemoveRewardsError;
    }

    return recoverableError;
  };

  private handleError(searchCriteriaStore, errorInfo) {
    let recoverableError;
    if (
      searchCriteriaStore.isSortAndFilterChanging ||
      searchCriteriaStore.showMoreSelected ||
      searchCriteriaStore.isLoyaltyToggleChanging
    ) {
      recoverableError = this.setRecoverableErrorState(searchCriteriaStore);
    } else {
      this.errorInFetchingResult = true;
    }
    
    if (!errorInfo){
      errorInfo = {
        message   : formatText('cars.changesearch.errorSummary.technicalError'),
        // TODO: to confirm actual value
        analytics : {
          __typename : 'ClientSideAnalytics',
          linkName   : 'technical-error',
          referrerId : 'CAR.SR.EmptyState.Search'
        }
      };
    }
    this.parsedErrorInfo = {
      ...errorInfo,
      isInvalidField : { ...this.isInvalidField, emptyState : true, errorType : recoverableError }
    };
   
    this.resetMap(searchCriteriaStore.searchVariables);
  }

  private displayErrors(response: any, searchCriteriaStore: SearchCriteriaStore){
    const { errors } = response;
    if (errors && errors.length > 0) {
      const event = errors && errors[0]?.extensions?.event;
      const name = event?.name.split(',');
      const { analytics: analyticsEvent } = event;
      if (SPINNERS_ERRORS[name]) {
        SPINNERS_ERRORS[name].split(',').map(fieldName => {
          this.isInvalidField = {
            ...this.isInvalidField,
            [fieldName] : true
          };
        });
      }
      const errorInfo = {
        message   : event.message,
        content   : event.content,
        analytics : analyticsEvent
      };
      this.handleError(searchCriteriaStore, errorInfo);
    } else {
      throw new Error('Response contains neither data nor error. Invalid response');
    }
  }

  private announceLoadingState(searchCriteriaStore: SearchCriteriaStore) {
    let lastLoadingStateMessage;
    let message;
    let level = Level.ASSERTIVE;
    if (
      this.isLoading ||
      searchCriteriaStore.isSortAndFilterChanging ||
      searchCriteriaStore.showMoreSelected ||
      searchCriteriaStore.isLoyaltyToggleChanging
    ) {
      message = formatText('cars.search.result.loading.announce');
    } else if (this.errorInFetchingResult) {
      message = formatText('cars.search.result.error.announce');
    } else if (this.offers && this.offers.length === 0) {
      message = formatText('cars.search.result.empty.announce');
    } else {
      message = formatText('cars.search.result.updated.announce');
      level = Level.POLITE;
    }
    if (message !== lastLoadingStateMessage) {
      liveAnnounce(message, level);
      lastLoadingStateMessage = message;
    }
  }
  @action
  public toggleBottomPageLoaderState(searchCriteriaStore: SearchCriteriaStore) {
    this.isLoading = !this.isLoading;
    searchCriteriaStore.showMoreSelected = this.isLoading;
  }

  @action
  public fireLoggingWithoutApiCall(analytics: AnalyticsStore) {
    analyticsUtil.resetAnalyticsPayLoad(analytics);
    analyticsUtil.publishAnalyticsPayload(this.response, analytics);
    //Prevent further page omniture caused by user interaction with sort and filter
    analyticsUtil.disableFurtherAnalyticsPayLoad();
    //No need to log page usable when api call is not made
    analyticsUtil.disablePageUsable();
  }

  @action
  public getSearchList(
    searchCriteriaStore: SearchCriteriaStore,
    pageId: string,
    analytics: AnalyticsStore,
    context: ContextStore,
    multiItemStore: MultiItemStore,
    domainServiceRedirect: boolean
  ): Promise<any> {
    const { searchVariables } = searchCriteriaStore;

    this.resetResults(searchCriteriaStore);
    this.announceLoadingState(searchCriteriaStore);

    /*
    analyticsUtil.publicCustomClientSideAnalyticsPayload(
      this.isApp ? PageId.CSR_LOADING_APP : PageId.CSR_LOADING_WEB,
      analytics,
      context.deviceId
    );
     */

    this.bexApiSource.headers = this.addDomainRedirectHeader(this.bexApiSource.headers, domainServiceRedirect);

    return this.bexApiSource.operations
      .carSearchV3(searchVariables, {
        pageId
      })
      .then(response => {
        runInAction('Car Search Results', () => {
          //response = require('../../.offline/mock-search-response.json');
          this.response = response;
          const responseData = response.data;
          if (responseData && responseData.carSearchOrRecommendations) {
            this.startResultsExpirationTimer(() => (this.timeOutCompleted = true));
            this.carSearchResult = responseData.carSearchOrRecommendations.carSearchResults;
            this.errorContent = responseData.carSearchOrRecommendations.carsErrorContent;
            this.recommendationContent = responseData.carSearchOrRecommendations.carRecommendationContent;

            this.recommendationsOnLimitedSearchResults = this.carSearchResult?.summary?.carRecommendations;

            const { errors } = response;
            if (errors && errors.length > 0) {
              const event = errors && errors[0]?.extensions?.event;
              if (event && event.content) {
                const { analytics: analyticsEvent } = event;
                this.parsedErrorInfo = {
                  message        : event.message,
                  content        : event.content,
                  analytics      : analyticsEvent,
                  isInvalidField : {
                    ...this.isInvalidField,
                    emptyState : true
                  }
                };
              }
            }

            if (this.errorContent  == null && this.recommendationContent == null && this.carSearchResult) {
              if (this.offers && this.offers.length > 0 && !searchCriteriaStore.isSortAndFilterChanging) {
                this.offers = this.offers.concat(this.carSearchResult.listings);
              } else {
                this.offers = this.carSearchResult.listings;
              }
              if (this.carSearchResult.multiItemAnchorPriceToken != null) {
                multiItemStore.anchorPriceToken = this.carSearchResult.multiItemAnchorPriceToken;
              }
              if (this.carSearchResult.shoppingContext?.multiItem != null) {
                multiItemStore.misId = this.carSearchResult.shoppingContext.multiItem.id;
              }
              if (this.carSearchResult.carsShoppingContext?.searchId != null ) {
                searchCriteriaStore.setSavedSearchId(this.carSearchResult.carsShoppingContext.searchId);
              }

              //Keep a deep copy of sortAndFilter in case we need revert selections without tampering the original response
              this.sortAndFilter = { ...JSON.parse(JSON.stringify(this.carSearchResult.sortAndFilter)) };
              this.appliedSortAndFilter = [...this.carSearchResult.carAppliedSortAndFilters.appliedSortAndFilters];
              this.carRentalLocation = { ...this.carSearchResult.carRentalLocations };
              this.map = this.carSearchResult.map;
              this.loadMore = this.carSearchResult.loadMoreAction;

              // Always use resolved location names from bex-api response unless it's not available
              searchCriteriaStore.searchQuery = {
                ...searchCriteriaStore.searchQuery,
                pickUpLoc :
                  this.carSearchResult.carRentalLocations.pickUpLocation.fullName ||
                  searchCriteriaStore.searchQuery.pickUpLoc,
                dropLoc :
                  this.carSearchResult.carRentalLocations.dropOffLocation.fullName ||
                  searchCriteriaStore.searchQuery.dropLoc,
                pickupRegion :
                  this.carSearchResult.carRentalLocations.pickUpLocation.regionId ||
                  searchCriteriaStore.searchQuery.pickupRegion,
                dropRegion :
                  this.carSearchResult.carRentalLocations.dropOffLocation.regionId ||
                  searchCriteriaStore.searchQuery.dropRegion
              };
            } else if (this.errorContent && this.recommendationContent == null) {
              const carsErrorContent = this.errorContent;
              let recoverableError;
              if (
                searchCriteriaStore.isSortAndFilterChanging ||
                searchCriteriaStore.showMoreSelected ||
                searchCriteriaStore.isLoyaltyToggleChanging
              ) {
                recoverableError = this.setRecoverableErrorState(searchCriteriaStore);
              } else {
                this.errorInFetchingResult = true;
              }
              const event = carsErrorContent;
              const name = event.errorEventName;
              if (SPINNERS_ERRORS[name]) {
                SPINNERS_ERRORS[name].split(',').map(fieldName => {
                  this.isInvalidField = {
                    ...this.isInvalidField,
                    [fieldName] : true
                  };
                });
              }
              this.parsedErrorInfo = {
                message        : event.heading,
                analytics      : undefined,
                isInvalidField : {
                  ...this.isInvalidField,
                  errorType : recoverableError
                }
              };
              this.resetMap(searchVariables);
            } else {
              this.resetMap(searchVariables);
            }
          } else {
            this.displayErrors(response, searchCriteriaStore);
          }
          analyticsUtil.publishAnalyticsPayload(response, analytics);
          analyticsUtil.disableFurtherAnalyticsPayLoad();
        });
      })
      .catch(error => {
        runInAction('recovering from bex api car detail availability call breakdown', () => {
          this.handleError(searchCriteriaStore, null);
          this.failureHandler(
            searchVariables,
            error,
            STORE_EVENTS.SEARCH_FETCH_ERROR,
            analytics,
            pageId,
            context.deviceId
          );
        });
        throw error;
      })
      .finally(() => {
        searchCriteriaStore.isSortAndFilterChanging = false;
        searchCriteriaStore.isLoyaltyToggleChanging = false;
        this.isLoading = false;
        this.searchOfferScrollPos = 0;
        searchCriteriaStore.showMoreSelected = false;
        this.announceLoadingState(searchCriteriaStore);
      });
  }
}
